'use client';

import { useState } from 'react';

import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

import { useLightboxContainer } from '@/components/providers/lightbox-container-provider';

interface ImageLightboxProps {
  images: string[];
  open: boolean;
  index: number;
  onClose: () => void;
}

export function ImageLightbox({ images, open, index, onClose }: ImageLightboxProps) {
  const slides = images.map(src => ({ src }));
  const isSingleImage = images.length <= 1;
  const portalRoot = useLightboxContainer() ?? undefined;

  return (
    <Lightbox
      open={open}
      close={onClose}
      slides={slides}
      index={index}
      carousel={{ finite: isSingleImage }}
      portal={{ root: portalRoot }}
      render={{
        buttonPrev: isSingleImage ? () => null : undefined,
        buttonNext: isSingleImage ? () => null : undefined,
      }}
      plugins={[Zoom]}
      zoom={{
        maxZoomPixelRatio: 3,
        zoomInMultiplier: 2,
        doubleTapDelay: 300,
        doubleClickDelay: 300,
        doubleClickMaxStops: 2,
        keyboardMoveDistance: 50,
        wheelZoomDistanceFactor: 100,
        pinchZoomDistanceFactor: 100,
        scrollToZoom: true,
      }}
    />
  );
}

interface UseImageLightboxReturn {
  lightboxProps: {
    open: boolean;
    index: number;
    onClose: () => void;
  };
  openLightbox: (index?: number) => void;
  createKeyHandler: (index: number) => (e: React.KeyboardEvent) => void;
}

export function useImageLightbox(): UseImageLightboxReturn {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const openLightbox = (newIndex: number = 0) => {
    setIndex(newIndex);
    setOpen(true);
  };

  const closeLightbox = () => {
    setOpen(false);
  };

  const createKeyHandler = (targetIndex: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openLightbox(targetIndex);
    }
  };

  return {
    lightboxProps: {
      open,
      index,
      onClose: closeLightbox,
    },
    openLightbox,
    createKeyHandler,
  };
}
